import type { ReactNode } from 'react'
import { cx } from './cx'

/**
 * A tinted message strip — the "something happened" banner, distinct from
 * `Card`, which is a white panel.
 *
 * Extracted from 44 hand-written copies of `bg-{fam}-50 border border-{fam}-200
 * text-{fam}-{700|800}` across the app. Three separate page migrations stopped
 * and reported "there is no Alert primitive" before this existed.
 *
 * Danger and warning carry `role="alert"` so a failure is announced rather than
 * only shown; success and info do not, since they are not interruptions.
 */
export type AlertTone = 'danger' | 'warning' | 'success' | 'accent'

const TONE: Record<AlertTone, string> = {
  danger: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  accent: 'bg-brand-50 border-brand-200 text-brand-700',
}

export default function Alert({
  tone = 'danger',
  title,
  className,
  children,
}: {
  tone?: AlertTone
  /** Bold first line, for the cases that need a heading above the body. */
  title?: ReactNode
  className?: string
  children?: ReactNode
}) {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : undefined}
      className={cx('rounded-lg border px-4 py-3 text-sm', TONE[tone], className)}
    >
      {title && <p className="font-semibold mb-1">{title}</p>}
      {children}
    </div>
  )
}
